/**
 * Shared DISPLAY ordering for a fanned hand of `<rank><suit>` CardIds — extracted
 * verbatim from HokmTable's `sortHand` (2026-07-10 Hearts rebuild) so every
 * trick-taking table fans identically: grouped by suit with alternating colours
 * (♠ پیک black, ♥ دل red, ♣ گشنیز black, ♦ خشت red) and each suit high→low.
 *
 * NOTE: HokmTable still carries its original private copy (that screen was
 * locked by a concurrent workstream when this module was extracted) — fold it
 * onto this import when it unlocks. Keep the two in lockstep until then.
 */

/** Suit order keyed by the CardId suit char (2nd char of `"AS"`/`"TD"`). */
const HAND_SUIT_ORDER: Record<string, number> = { S: 0, H: 1, C: 2, D: 3 };

/** Rank value for a CardId rank char (1st char), Ace high — sorts each suit high→low. */
const HAND_RANK_VALUE: Record<string, number> = {
  A: 14,
  K: 13,
  Q: 12,
  J: 11,
  T: 10,
  "9": 9,
  "8": 8,
  "7": 7,
  "6": 6,
  "5": 5,
  "4": 4,
  "3": 3,
  "2": 2,
};

/**
 * Sort a hand for DISPLAY only — grouped by suit (♠♥♣♦, alternating colours) and,
 * within each suit, high→low by rank. Derive it fresh from `view.hand` on every
 * render (the server order is not relied upon), so the fan stays sorted after
 * each play. This changes render order alone; the CardId → move wiring is untouched.
 */
export function sortHand(
  hand: readonly string[],
  /** Override the suit grouping (2nd CardId char → order index). Defaults to the
   * shared ♠♥♣♦ order; Bidel passes its own پیک·خشت·گشنیز·دل order. */
  suitOrder: Record<string, number> = HAND_SUIT_ORDER,
): string[] {
  return [...hand].sort((a, b) => {
    const suitA = suitOrder[a.charAt(1)] ?? 99;
    const suitB = suitOrder[b.charAt(1)] ?? 99;
    if (suitA !== suitB) return suitA - suitB;
    const rankA = HAND_RANK_VALUE[a.charAt(0)] ?? 0;
    const rankB = HAND_RANK_VALUE[b.charAt(0)] ?? 0;
    return rankB - rankA; // high → low within the suit
  });
}
